/** Optional, GA4-only analytics. Never reuse the marketing GTM/dataLayer here. */
const ID = 'G-VBXBLHKYRM';
const SAFE_LOCATION = 'https://v2.ondiet.app/';
const COOKIE_OWNER = 'ondiet-usage-cookie-owner-v1';
const CHANGE = 'ondiet-analytics-preference';
const EVENTS = new Set(['quiz_start', 'quiz_step', 'quiz_lead', 'quiz_preview', 'quiz_checkout', 'quiz_checkout_erro', 'peso_tela_vista', 'peso_meta_concluida', 'peso_projecao_concluida', 'peso_validacao_erro', 'peso_card_visto', 'peso_registro_iniciado', 'peso_registro_salvo', 'peso_meta_salva', 'gema_exposed', 'gema_intro_viewed', 'gema_intro_completed', 'gema_collection_opened', 'gema_item_equipped', 'gema_player_failed']);
declare global { interface Window { ondietAnalytics?: IArguments[]; } }
let account: string | null = null;
let key: string | null = null;
let allowed = false;
let ready = false;
let generation = 0;
let script: HTMLScriptElement | null = null;
const recentViews = new Map<string, number>();
let loadedThisPage = false;
let pending: Array<[string, Record<string, unknown>]> = [];
function flush() {
  if (!ready || !allowed) return;
  for (const [event, params] of pending.splice(0)) command('event', event, params);
}
const permittedHost = () => import.meta.env.PROD && window.location.hostname === 'v2.ondiet.app';
// gtag's documented queue protocol uses Arguments objects, not GTM event objects.
function command(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  (window.ondietAnalytics ??= []).push(arguments);
}
const notify = () => window.dispatchEvent(new Event(CHANGE));
export const subscribeAnalytics = (listener: () => void) => { window.addEventListener(CHANGE, listener); return () => window.removeEventListener(CHANGE, listener); };
export const analyticsAllowed = () => allowed;
export const analyticsPreferenceReady = () => key !== null;

function stop(clear = true) {
  allowed = false;
  ready = false;
  recentViews.clear();
  pending = [];
  // GA's documented disable switch also suppresses automatic events after revocation.
  (window as unknown as Record<string, unknown>)[`ga-disable-${ID}`] = true;
  script?.remove(); script = null;
  window.ondietAnalytics = [];
  if (clear) clearCookies();
}
function clearCookies() {
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.trim().split('=')[0];
    if (!name.startsWith('ondiet_app_ga')) continue;
    for (const domain of ['', ';domain=v2.ondiet.app']) {
      document.cookie = `${name}=;max-age=0;path=/${domain};SameSite=Lax;Secure`;
    }
  }
}
function start() {
  if (!permittedHost() || !allowed || script) return;
  // Preserve continuity on reload only for the same locally scoped account.
  try {
    if (localStorage.getItem(COOKIE_OWNER) !== key) clearCookies();
    localStorage.setItem(COOKIE_OWNER, key!);
  } catch { allowed = false; clearCookies(); return; }
  loadedThisPage = true;
  const current = generation;
  (window as unknown as Record<string, unknown>)[`ga-disable-${ID}`] = false;
  command('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  command('js', new Date());
  command('config', ID, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false, cookie_domain: 'v2.ondiet.app', cookie_prefix: 'ondiet_app', page_location: SAFE_LOCATION, page_referrer: '', page_title: 'OnDiet App', user_id: null });
  script = document.createElement('script');
  script.async = true;
  script.referrerPolicy = 'no-referrer';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ID}&l=ondietAnalytics`;
  script.onload = () => { if (current === generation && allowed) { ready = true; flush(); } };
  script.onerror = () => {
    if (current !== generation) return;
    ready = false; script?.remove(); script = null; loadedThisPage = false;
    window.ondietAnalytics = []; pending = []; recentViews.clear();
  };
  document.head.appendChild(script);
}
/** Called synchronously by the auth snapshot, before consumers can emit events. */
export async function setAnalyticsAccount(next: string | null): Promise<void> {
  if (next === account) return;
  const changedAccount = account !== null;
  account = next; const current = ++generation;
  stop(changedAccount); key = null; notify();
  // Reload tears down Google listeners and in-memory identifiers between accounts.
  // Logout must finish persisting Supabase sign-out before any navigation.
  if (!next) return;
  if (loadedThisPage) { window.location.reload(); return; }
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(next));
    if (current !== generation) return;
    key = `ondiet-usage-v1:${Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, '0')).join('')}`;
    allowed = localStorage.getItem(key) === 'yes';
    if (!allowed) { pending = []; recentViews.clear(); clearCookies(); }
    start(); notify();
  } catch { key = null; allowed = false; notify(); }
}
export function setAnalyticsConsent(value: boolean): boolean {
  if (!key) return false;
  try { localStorage.setItem(key, value ? 'yes' : 'no'); } catch { return false; }
  ++generation; stop(); allowed = value; notify();
  // Removing a script cannot remove its listeners. A new document can.
  if (loadedThisPage) { window.location.reload(); return true; }
  start(); return true;
}
export function trackApp(event: string, params: Record<string, unknown> = {}): void {
  if (!account || (!allowed && key !== null) || !permittedHost() || !EVENTS.has(event)) return;
  const safe: Record<string, unknown> = { origem: 'app', send_to: ID, page_location: SAFE_LOCATION, page_referrer: '', page_title: 'OnDiet App' };
  if ((event === 'quiz_step' || event === 'peso_tela_vista') && typeof params.step_id === 'string' && /^(T\d{2}[a-z]?|P0[12]|I[1-6])$/.test(params.step_id)) safe.step_id = params.step_id;
  if (event === 'quiz_step' && Number.isInteger(params.step_index) && Number(params.step_index) >= 0 && Number(params.step_index) <= 100) safe.step_index = params.step_index;
  if (event.startsWith('gema_')) {
    if (params.version === 'gema-launch-v1') safe.version = params.version;
    if (typeof params.source === 'string' && params.source.length <= 32) safe.source = params.source;
    if (typeof params.item_id === 'string' && ['bow', 'glasses', 'skirt', 'original'].includes(params.item_id)) safe.item_id = params.item_id;
    if (params.renderer === 'video' || params.renderer === 'rive') safe.renderer = params.renderer;
  }
  const view = event === 'quiz_step' || event === 'peso_card_visto' || event === 'peso_tela_vista';
  const signature = `${event}:${safe.step_id ?? ''}`;
  if (view && Date.now() - (recentViews.get(signature) ?? 0) < 500) return;
  if (view) recentViews.set(signature, Date.now());
  // While the stored preference resolves, only keep sanitized current-account
  // events; discard them unless this account had already opted in. A new
  // opt-in always clears this buffer, so it cannot replay pre-consent actions.
  pending.push([event, safe]);
  if (pending.length > 100) pending.shift();
  if (allowed && !script) start();
  flush();
}

// A revocation in another tab applies immediately in this tab as well.
window.addEventListener('storage', event => {
  if (!key || (event.key !== key && event.key !== null)) return;
  if (event.newValue === 'yes') return; // Opt-in elsewhere never starts tracking here.
  ++generation; stop(); notify();
  if (loadedThisPage) window.location.reload();
});
